import { useEffect, useMemo, useState } from "react";
import { firstErrorMessage } from "../helpers/apiErrors";
import { useMutation, useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import {
  BadgeCheck,
  Building2,
  CreditCard,
  Mail,
  MapPinned,
  Phone,
  Save,
  Sparkles,
  UserRound,
  X,
  LucideEdit,
} from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";

import { FormItem, FormMessage } from "./common/ui/form";
import { Label } from "./common/ui/label";
import { getUser, updateUser } from "../services";
import useReactForm from "../hooks/useForm";
import { Button } from "./common/ui/button";
import { Input } from "./common/ui/input";
import { useToast } from "./common/ui/use-toast";
import validate from "../helpers/validate";
import { useAuth } from "src/hooks/useAuth";
import { useGetUserPaymentMethods } from "src/hooks/useTrading";
import AddProfileMethods from "./common/AddPaymentMethods";

const READ_ONLY_FIELDS = new Set(["email", "phone", "country"]);
type ProfileFieldName =
  | "firstname"
  | "lastname"
  | "trading_name"
  | "email"
  | "phone"
  | "country"
  | "address"
  | "city";

type ProfileFieldDefinition = {
  name: ProfileFieldName;
  label: string;
  placeholder: string;
  type?: string;
};

const PROFILE_FIELDS: ProfileFieldDefinition[] = [
  { name: "firstname", label: "First name", placeholder: "John" },
  { name: "lastname", label: "Last name", placeholder: "Doe" },
  { name: "trading_name", label: "Trade name", placeholder: "Blue Corner" },
  { name: "email", label: "Email", placeholder: "name@example.com", type: "email" },
  { name: "phone", label: "Phone number", placeholder: "0248360953", type: "tel" },
  { name: "country", label: "Country", placeholder: "Ghana" },
  { name: "address", label: "Address", placeholder: "Street address" },
  { name: "city", label: "City", placeholder: "Accra" },
];

const Profile = () => {
  const { toast } = useToast();
  const methods = useForm();
  const { values, setValues, errors, handleChange } = useReactForm(validate);

  const [isEditing, setIsEditing] = useState(false);
  const [savedValues, setSavedValues] = useState<any>({});
  const [showAddPaymentOption, setShowAddPaymentOption] = useState(false);
  const [focusedPayment, setFocusedPayment] = useState({});

  const showAddOrEditPaymentOption =
    showAddPaymentOption || Object.values(focusedPayment).length > 0;

  const { data: paymentMethodsResponse } = useGetUserPaymentMethods();
  const paymentMethods = paymentMethodsResponse?.results || [];

  const {
    isLoading,
    isError,
    data: userDataResponse,
    refetch,
  } = useQuery({
    queryKey: ["userDetails"],
    queryFn: () => getUser(),
  });

  const userData = userDataResponse || {};
  const userLoaded = userData && Object.values(userData).length > 0;

  useEffect(() => {
    const nextValues = {
      firstname: userData?.firstname || "",
      lastname: userData?.lastname || "",
      email: userData?.email || "",
      phone: userData?.phone || "",
      country: userData?.country || "",
      address: userData?.address || "",
      city: userData?.city || "",
      trading_name: userData?.trading_name || "",
    };

    if (userLoaded) {
      setValues(nextValues);
      setSavedValues(nextValues);
      setIsEditing(false);
    }
  }, [setValues, userData, userLoaded]);

  const hasChanges = useMemo(() => {
    const fieldNames = ["firstname", "lastname", "email", "phone", "country", "address", "city", "trading_name"];
    return fieldNames.some(
      (field) => String(values?.[field] ?? "") !== String(savedValues?.[field] ?? ""),
    );
  }, [savedValues, values]);

  const completionProgress = useMemo(() => {
    const tracked = ["firstname", "lastname", "address", "city", "trading_name"];
    const completed = tracked.filter((field) => String(userData?.[field] ?? "").trim()).length;
    return Math.round((completed / tracked.length) * 100);
  }, [userData]);

  const createMutation = useMutation({
    mutationFn: (payload) => updateUser(userData?.id, payload),
    onSuccess: async () => {
      await refetch();
      setSavedValues({
        firstname: values?.firstname || "",
        lastname: values?.lastname || "",
        email: values?.email || "",
        phone: values?.phone || "",
        country: values?.country || "",
        address: values?.address || "",
        city: values?.city || "",
        trading_name: values?.trading_name || "",
      });
      setIsEditing(false);
      toast({
        variant: "default",
        description: "Your details were updated successfully.",
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data;
      if (message && typeof message === "object") {
        Object.keys(message).forEach((field) => {
          toast({
            variant: "destructive",
            title: `${field.charAt(0).toUpperCase() + field.slice(1)}`,
            description: firstErrorMessage(message[field]),
          });
        });
      } else {
        toast({
          variant: "destructive",
          title: "Uh oh! Something went wrong.",
          description: "There was a problem with your request.",
        });
      }
    },
  });

  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    window.history.replaceState(null, "", "/");
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasChanges) {
      return;
    }

    createMutation.mutate({
      ...values,
    });
  };

  const fieldErrors: Record<string, string | undefined> = {
    firstname: errors?.firstNameErrorMessage,
    lastname: errors?.lastNameErrorMessage,
    phone: errors?.phoneErrorMessage,
    address: errors?.addressErrorMessage,
    city: errors?.cityErrorMessage,
  };

  const renderField = (field: ProfileFieldDefinition) => {
    const currentValue = values?.[field.name] ?? userData?.[field.name] ?? "";
    const isReadonly = READ_ONLY_FIELDS.has(field.name);

    if (!isEditing || isReadonly) {
      return (
        <DetailItem key={field.name}>
          <DetailLabel>{field.label}</DetailLabel>
          <DetailValue>{String(currentValue || "Not set")}</DetailValue>
        </DetailItem>
      );
    }

    return (
      <FormItem key={field.name}>
        <Label>{field.label}</Label>
        <Input
          type={field.type || "text"}
          name={field.name}
          placeholder={field.placeholder}
          onChange={handleChange}
          value={String(currentValue || "")}
        />
        {fieldErrors[field.name] && <FormMessage>{fieldErrors[field.name]}</FormMessage>}
      </FormItem>
    );
  };

  const personalFields = PROFILE_FIELDS.filter(({ name }) => name === "firstname" || name === "lastname");
  const businessFields = PROFILE_FIELDS.filter(({ name }) => name === "trading_name");
  const contactFields = PROFILE_FIELDS.filter(({ name }) => name === "email" || name === "phone");
  const addressFields = PROFILE_FIELDS.filter(
    ({ name }) => name === "country" || name === "address" || name === "city",
  );

  const discardChanges = () => {
    setValues(savedValues);
    setIsEditing(false);
  };

  return (
    <ProfileShell>
      <ProfileHeader>
        <div>
          <ProfileTitle>Personal details</ProfileTitle>
        </div>

        <HeaderActions>
          <StatusBadge $tone={completionProgress === 100 ? "emerald" : "amber"}>
            <Sparkles size={14} />
            {completionProgress}% complete
          </StatusBadge>
          {isEditing ? (
            <Button type="button" variant="outline" onClick={discardChanges}>
              <X size={15} />
              Cancel editing
            </Button>
          ) : (
            <Button type="button" onClick={() => setIsEditing(true)}>
              <LucideEdit size={15} />
              Edit profile
            </Button>
          )}
        </HeaderActions>
      </ProfileHeader>

      {isLoading && <StateCard>Loading your profile...</StateCard>}
      {isError && (
        <StateCard>
          <p>There was an error fetching user data.</p>
          <Button type="button" onClick={() => refetch()} variant="destructive">
            Retry
          </Button>
        </StateCard>
      )}

      {!isLoading && !isError && userData && (
        <FormProvider {...methods}>
          <ProfileForm onSubmit={handleSubmit}>
            <ProfileGrid>
              <SectionCard>
                <SectionCardHeader>
                  <SectionCardIcon>
                    <UserRound size={16} />
                  </SectionCardIcon>
                  <div>
                    <SectionTitle>Personal</SectionTitle>
                    <SectionDescription>Name and identity.</SectionDescription>
                  </div>
                </SectionCardHeader>
                <FieldGrid>
                  {personalFields.map(renderField)}
                </FieldGrid>
              </SectionCard>

              <SectionCard>
                <SectionCardHeader>
                  <SectionCardIcon>
                    <Building2 size={16} />
                  </SectionCardIcon>
                  <div>
                    <SectionTitle>Business</SectionTitle>
                    <SectionDescription>Your trading name.</SectionDescription>
                  </div>
                </SectionCardHeader>
                <FieldGrid>{businessFields.map(renderField)}</FieldGrid>
              </SectionCard>

              <SectionCard>
                <SectionCardHeader>
                  <SectionCardIcon>
                    <Mail size={16} />
                  </SectionCardIcon>
                  <div>
                    <SectionTitle>Contact</SectionTitle>
                    <SectionDescription>Email and phone number.</SectionDescription>
                  </div>
                </SectionCardHeader>
                <FieldGrid>{contactFields.map(renderField)}</FieldGrid>
              </SectionCard>

              <SectionCard>
                <SectionCardHeader>
                  <SectionCardIcon>
                    <MapPinned size={16} />
                  </SectionCardIcon>
                  <div>
                    <SectionTitle>Address</SectionTitle>
                    <SectionDescription>Country, city, and street address.</SectionDescription>
                  </div>
                </SectionCardHeader>
                <FieldGrid>{addressFields.map(renderField)}</FieldGrid>
              </SectionCard>
            </ProfileGrid>

            <SaveBar $visible={isEditing}>
              <div>
                <SaveLabel>{hasChanges ? "Unsaved changes" : "No changes yet"}</SaveLabel>
                <SaveCopy>
                  Save to update your profile.
                </SaveCopy>
              </div>
              <SaveActions>
                <Button type="button" variant="outline" onClick={discardChanges}>
                  Discard
                </Button>
                <Button type="submit" disabled={!hasChanges || createMutation.isPending}>
                  <Save size={15} />
                  {createMutation.isPending ? "Saving..." : "Save changes"}
                </Button>
              </SaveActions>
            </SaveBar>
          </ProfileForm>

          <MethodsSection>
            <MethodsHeader>
              <div>
                <MethodsTitle>Payment methods</MethodsTitle>
              </div>
              <Button
                type="button"
                onClick={() => setShowAddPaymentOption(true)}
                variant="outline"
              >
                <CreditCard size={15} />
                Add payment option
              </Button>
            </MethodsHeader>

            {paymentMethods?.length ? (
              <MethodsList>
                {paymentMethods?.map((option: any) => {
                  const { id, account_details, payment_option } = option;
                  return (
                    <MethodRow key={id}>
                      <MethodMeta>
                        <MethodTitle>{payment_option?.name || "--"}</MethodTitle>
                        <MethodLines>
                          <span>Account name: {account_details?.name || "—"}</span>
                          <span>Account number: {account_details?.account_number || "—"}</span>
                        </MethodLines>
                      </MethodMeta>
                      <Button
                        type="button"
                        onClick={() => setFocusedPayment(option)}
                        disabled={Boolean(createMutation.isPending)}
                        variant="outline"
                      >
                        <LucideEdit size={14} />
                        Edit
                      </Button>
                    </MethodRow>
                  );
                })}
              </MethodsList>
            ) : (
              <EmptyState>
                <BadgeCheck size={18} />
                <p>No payment methods have been added yet.</p>
              </EmptyState>
            )}
          </MethodsSection>
        </FormProvider>
      )}

      {showAddOrEditPaymentOption && (
        <AddProfileMethods
          open={showAddOrEditPaymentOption}
          onClose={() => {
            setShowAddPaymentOption(false);
            setFocusedPayment({});
          }}
          focusedPayment={focusedPayment}
        />
      )}
    </ProfileShell>
  );
};

const ProfileShell = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
`;

const ProfileHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ProfileTitle = styled.h2`
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  font-size: clamp(1.5rem, 2vw, 2rem);
  line-height: 1.15;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  justify-content: flex-end;

  button {
    white-space: nowrap;
  }
`;

const StatusBadge = styled.span<{ $tone: "emerald" | "amber" }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  background: ${(props) => (props.$tone === "emerald" ? "rgba(16, 185, 129, 0.12)" : "rgba(245, 158, 11, 0.12)")};
  color: ${(props) => (props.$tone === "emerald" ? "#047857" : "#b45309")};
`;

const StateCard = styled.div`
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(255, 255, 255, 0.92);
  border-radius: 20px;
  padding: 1rem;
  color: ${(props) => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const ProfileForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SectionCard = styled.section`
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(250, 251, 253, 0.98));
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 24px;
  padding: 1.1rem;
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.04);
`;

const SectionCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: 1rem;
`;

const SectionCardIcon = styled.div`
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: rgba(225, 29, 72, 0.1);
  color: #e11d48;
  flex-shrink: 0;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  color: ${(props) => props.theme.colors.text};
`;

const SectionDescription = styled.p`
  margin: 0.2rem 0 0;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.88rem;
  line-height: 1.6;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  padding: 0.95rem;
  border-radius: 18px;
  background: rgba(248, 250, 252, 0.95);
  border: 1px solid rgba(226, 232, 240, 0.9);
`;

const DetailLabel = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
`;

const DetailValue = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  font-size: 0.98rem;
  line-height: 1.55;
  word-break: break-word;
`;

const SaveBar = styled.div<{ $visible: boolean }>`
  position: sticky;
  bottom: 1rem;
  z-index: 12;
  display: ${(props) => (props.$visible ? "flex" : "none")};
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border-radius: 22px;
  border: 1px solid rgba(225, 29, 72, 0.18);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(255, 241, 242, 0.96));
  box-shadow: 0 20px 40px rgba(225, 29, 72, 0.1);
  backdrop-filter: blur(14px);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SaveLabel = styled.h4`
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  font-size: 1rem;
`;

const SaveCopy = styled.p`
  margin: 0.25rem 0 0;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.86rem;
  line-height: 1.55;
`;

const SaveActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-wrap: wrap;

  button {
    white-space: nowrap;
  }
`;

const MethodsSection = styled.section`
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(255, 255, 255, 0.96);
  padding: 1.2rem;
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.04);
`;

const MethodsHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MethodsTitle = styled.h3`
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  font-size: 1.1rem;
`;

const MethodsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const MethodRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98));

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const MethodMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
`;

const MethodTitle = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  font-weight: 700;
  font-size: 0.98rem;
`;

const MethodLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.86rem;
  line-height: 1.5;
  word-break: break-word;
`;

const EmptyState = styled.div`
  border: 1px dashed rgba(148, 163, 184, 0.35);
  border-radius: 18px;
  padding: 1.2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${(props) => props.theme.colors.textSecondary};
  background: rgba(248, 250, 252, 0.8);
`;

export default Profile;
